<template>
    <div id="SkillView">
        <OpportunityPageLayout
            v-if="isVisible"
            back-label="Späť na vzdelávanie"
            reverse-bottom-style
            :to="{
                name: 'vzdelavanie',
                query: route.query,
                hash: `#r-id-${props.skill.id}`
            }"
        >
            <SkillViewLayoutTop :org="org" :skill="skill" />

            <template #middle>
                <SkillViewLayoutMiddle :org="org" :skill="skill" />
            </template>

            <template v-if="skill.content && skill.content.length" #left>
                <SkillViewLayoutLeft :skill="skill" />
            </template>

            <template v-if="!isStudyOpportunity && skill.author" #right>
                <SkillViewLayoutRight :skill="skill" />
            </template>
        </OpportunityPageLayout>

        <div v-else>
            <NotFound title="Ľutujeme">
                táto ponuka už nie je aktuálna. <br />
                Zobraziť všetky
                <NuxtLink
                    class="link"
                    :to="{
                        name: 'vzdelavanie'
                    }"
                >
                    aktuálne ponuky</NuxtLink
                >.
            </NotFound>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import type { PropType } from 'vue'

import { SkillType } from '#shared/types/Skill'
import type { Skill, SkillOrgPreview } from '#shared/types/Skill'
import { VisibilityType } from '#shared/types/visibility'
import { OrgType } from '#shared/types/Org'
import NotFound from '@/components/common/NotFound.vue'
import OpportunityPageLayout from '@/components/common/opportunity/OpportunityPageLayout.vue'
import SkillViewLayoutLeft from '@/components/skills/SkillViewLayoutLeft.vue'
import SkillViewLayoutMiddle from '@/components/skills/SkillViewLayoutMiddle.vue'
import SkillViewLayoutRight from '@/components/skills/SkillViewLayoutRight.vue'
import SkillViewLayoutTop from '@/components/skills/SkillViewLayoutTop.vue'
import { useOrgs } from '@/composables/orgs/useOrgs'

const props = defineProps({
    skill: {
        type: Object as PropType<Skill>,
        required: true
    }
})

const route = useRoute()
// Starts from the org snapshot embedded in the skill; the full org (members) is loaded on the
// client after hydration, so SSR and the first client render show the same markup.
const org = ref<SkillOrgPreview>(buildOrgSnapshot())
const { loadOrg } = useOrgs()

function buildOrgSnapshot(): SkillOrgPreview {
    return {
        id: props.skill.orgMeta?.id || props.skill.orgId || '',
        slug: props.skill.orgMeta?.slug || '',
        title: props.skill.orgMeta?.title || '',
        type: props.skill.orgMeta?.type || OrgType.other,
        memberIds: []
    }
}

async function fetchData() {
    try {
        const orgId = props.skill.orgId
        if (!orgId) {
            return
        }

        const loadedOrg = await loadOrg(orgId)
        org.value = {
            id: loadedOrg.id,
            slug: loadedOrg.slug || '',
            title: loadedOrg.title || org.value.title,
            type: loadedOrg.type || org.value.type,
            memberIds: loadedOrg.memberIds || []
        }
    } catch (error) {
        console.warn('Unable to load organization for skill detail, using embedded org meta.', error)
    }
}

onMounted(fetchData)

const isVisible = computed(() => {
    return props.skill.visibility !== VisibilityType.hidden
})

const isStudyOpportunity = computed(() => {
    return props.skill.type === SkillType.studyOpportunity
})
</script>
